import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import './FAQ.css';

// API Configuration
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

const FAQPage = () => {
  const [faqCategories, setFaqCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedItems, setExpandedItems] = useState({});

  // Helper function to strip HTML tags from text
  const stripHtmlTags = (html) => {
    return html.replace(/<[^>]*>/g, '');
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/faq/public`);
      
      if (response.data.success) {
        setFaqCategories(response.data.data);
      } else {
        setError('Erro ao carregar perguntas frequentes.');
      }
    } catch (error) {
      console.error('Erro ao buscar FAQs:', error);
      setError('Erro ao carregar perguntas frequentes. Por favor, tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (categoryId, questionId) => {
    const key = `${categoryId}-${questionId}`;
    setExpandedItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Filter categories and questions based on search and selected category
  const getFilteredData = () => {
    let filtered = [...faqCategories];
    
    // Filter by selected category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(cat => cat.id === selectedCategory);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.map(category => ({
        ...category,
        questions: category.questions.filter(q =>
          q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.answer.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })).filter(category => category.questions.length > 0);
    }
    
    return filtered;
  };

  const filteredData = getFilteredData();

  // Get total question count for display
  const getTotalQuestions = () => {
    return faqCategories.reduce((total, cat) => total + cat.questions.length, 0);
  };

  // Handle category button click
  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setSearchTerm(''); // Clear search when selecting category
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value) {
      setSelectedCategory('all'); // Reset to all categories when searching
    }
  };

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <section className="faq-hero">
        <div className="faq-hero-content">
          <h1>Perguntas Frequentes</h1>
          <p>
            Encontre respostas para suas dúvidas sobre registros comerciais,
            atos societários e os serviços oferecidos pela Veridicus.
          </p>
        </div>
      </section>
      
      {/* Search Section */}
      <section className="faq-search-section">
        <div className="faq-search-container">
          <div className="search-wrapper">
            <input
              type="text"
              placeholder="Buscar perguntas..."
              value={searchTerm}
              onChange={handleSearch}
              className="faq-search-input"
            />
          </div>
        </div>
      </section>
      
      {/* Main Content with Sidebar */}
      <section className="faq-content">
        <div className="faq-container">
          <div className="faq-layout">
            {/* Sidebar Categories */}
            {!loading && !error && (
              <aside className="faq-sidebar">
                <div className="sidebar-categories">
                  <div 
                    className={`category-card ${selectedCategory === 'all' ? 'active' : ''}`}
                    onClick={() => handleCategoryClick('all')}
                  >
                    <h3>Todas as Categorias</h3>
                    <span className="category-count">{getTotalQuestions()} perguntas</span>
                  </div>
                  
                  {faqCategories.map(category => (
                    <div 
                      key={category.id}
                      className={`category-card ${selectedCategory === category.id ? 'active' : ''}`}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      <h3>{category.title}</h3>
                      <span className="category-count">
                        {category.questions.length} {category.questions.length === 1 ? 'pergunta' : 'perguntas'}
                      </span>
                    </div>
                  ))}
                </div>
              </aside>
            )}
            
            {/* FAQ Content */}
            <div className="faq-main-content">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Carregando perguntas frequentes...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <p>{error}</p>
                  <button onClick={fetchFAQs} className="retry-button">
                    Tentar Novamente
                  </button>
                </div>
              ) : filteredData.length === 0 ? (
                <div className="empty-state">
                  <h3>Nenhuma pergunta encontrada</h3>
                  <p>
                    {searchTerm 
                      ? `Não encontramos perguntas para "${searchTerm}"`
                      : 'Não há perguntas disponíveis no momento'}
                  </p>
                  {searchTerm && (
                    <button 
                      onClick={() => setSearchTerm('')} 
                      className="clear-search-button"
                    >
                      Limpar Busca
                    </button>
                  )}
                </div>
              ) : (
                <div className="faq-categories">
                  {filteredData.map(category => (
                    <div key={category.id} className="faq-category">
                      <div className="category-header">
                        <h2>{category.title}</h2>
                        <span className="question-count">
                          {category.questions.length} {category.questions.length === 1 ? 'pergunta' : 'perguntas'}
                        </span>
                      </div>
                      
                      <div className="faq-items">
                        {category.questions.map(item => {
                          const isExpanded = expandedItems[`${category.id}-${item.id}`];
                          
                          return (
                            <div 
                              key={item.id} 
                              className={`faq-item ${isExpanded ? 'expanded' : ''}`}
                            >
                              <button
                                className="faq-question"
                                onClick={() => toggleItem(category.id, item.id)}
                              >
                                <span>{item.question}</span>
                                <span className="faq-toggle">
                                  {isExpanded ? '−' : '+'}
                                </span>
                              </button>
                              
                              <div className={`faq-answer ${isExpanded ? 'show' : ''}`}>
                                <p>{stripHtmlTags(item.answer)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="faq-cta">
        <div className="faq-cta-content">
          <h2>Não encontrou o que procurava?</h2>
          <p>
            Nossa equipe está pronta para esclarecer suas dúvidas e 
            oferecer o suporte necessário para sua empresa.
          </p>
          <div className="faq-cta-buttons">
            <Link to="/fale-conosco" className="cta-button primary">
              Fale Conosco
            </Link>
            <a href="mailto:comercial@veridicus.com.br" className="cta-button secondary">
              Enviar Email
            </a>
          </div>
        </div>
      </section>
      
      <Footer />
    </>
  );
};

export default FAQPage;